import React, { type ReactNode, forwardRef, useState } from 'react'
import { css } from '@emotion/react'
import get from 'lodash/get'
import { fontSize, spacing } from '@retailer-platform/shared-components'
import { MerchandisingFormInputWrapper } from 'domains/merchandising/components/form-input/MerchandisingFormInputWrapper'
import { useDashIntl, useDashMessage } from 'intl/intl.hooks'
import { type DashMessages } from 'intl/intl.types'
import { Input, type InputProps } from 'gin-and-tonic/common/input/Input'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import {
  type FormErrorMessageMap,
  type ErrorRecordType,
} from 'utils/react-hook-form/reactHookForm.types'

export interface Props extends Omit<InputProps, 'name'>, Pick<ControllerProps, 'control'> {
  identifier?: string
  labelId?: DashMessages
  labelPrefix?: ReactNode
  showLength?: boolean
  errors?: ErrorRecordType
  errorMessages?: FormErrorMessageMap
}

const defaultErrorMessages: Record<string, DashMessages> = {
  required: 'merchandising.hero-banners.form.validation.required',
}

const outerStyles = css({
  margin: '0px',
})

const inputStyles = css({
  height: 46,
  fontSize: fontSize.X15,

  // TODO: Swap out input error styles for custom ErrorMessage
  '& + div:not(:empty)': {
    marginTop: spacing.X4,
    marginLeft: 0,
    marginBottom: 0,
  },
})

export const MerchandisingFormInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      errors = {},
      maxLength,
      labelId,
      labelPrefix = '',
      identifier,
      onChange,
      defaultValue = '',
      showLength,
      ...rest
    },
    ref
  ) => {
    const label = labelPrefix + useDashMessage(labelId)

    const [value, setValue] = useState(defaultValue)

    const onTextChange = event => {
      onChange?.(event)
      setValue(event.target.value)
    }

    const intl = useDashIntl()

    const errorMessageId = useFormErrorMessageMap(defaultErrorMessages)(get(errors, identifier))
    const formattedMessage = errorMessageId && intl.formatMessage({ id: errorMessageId }, { label })

    return (
      <MerchandisingFormInputWrapper
        label={label}
        maxLength={maxLength}
        showLength={showLength}
        value={value.toString()}
      >
        <Input
          {...rest}
          ref={ref}
          name={identifier}
          maxLength={maxLength}
          onChange={onTextChange}
          placeholder={label}
          styles={inputStyles}
          outerStyles={outerStyles}
          // Default value only allowed for controlled components
          defaultValue={ref ? undefined : defaultValue}
          errorMessage={formattedMessage}
          errorMessageId={
            errorMessageId ? 'merchandising.hero-banners.form.validation.default' : null
          }
        />
      </MerchandisingFormInputWrapper>
    )
  }
)
